import * as Phaser from "phaser";
import { CancelablePromise } from "cancelable-promise";
import type { SuperLoaderPlugin } from "../Services/SuperLoaderPlugin";
import { PlayerTexturesKey } from "./PlayerTextures";
import type { WokaTextureDescriptionInterface, PlayerTextures } from "./PlayerTextures";

import Texture = Phaser.Textures.Texture;
import LoaderPlugin = Phaser.Loader.LoaderPlugin;

export interface FrameConfig {
    frameWidth: number;
    frameHeight: number;
}

/**
 * MERCADOLOGICA - Woka HD
 *
 * Normal:
 *   96x128   = frames 32x32
 *
 * hd2x_:
 *   192x256  = frames 64x64
 *
 * hd4x_:
 *   384x512  = frames 128x128
 *
 * hd8x_:
 *   768x1024 = frames 256x256
 */
export function getPlayerTextureFrameSize(textureId: string): number {
    if (textureId.startsWith("hd8x_")) {
        return 256;
    }

    if (textureId.startsWith("hd4x_")) {
        return 128;
    }

    if (textureId.startsWith("hd2x_")) {
        return 64;
    }

    return 32;
}

export function getPlayerTextureRenderScale(textureId: string): number {
    const baseScale = 32 / getPlayerTextureFrameSize(textureId);

    // MERCADOLOGICA:
    // Avatares HD8x ficam 15% maiores visualmente.
    // 0.125 -> 0.158125
    if (textureId.startsWith("hd8x_")) {
        return baseScale * 1.265;
    }

    return baseScale;
}

export const loadAllLayers = (
    load: LoaderPlugin,
    playerTextures: PlayerTextures,
): WokaTextureDescriptionInterface[][] => {
    const returnArray: WokaTextureDescriptionInterface[][] = [];
    playerTextures.getLayers().forEach((layer) => {
        const layerArray: WokaTextureDescriptionInterface[] = [];
        Object.values(layer).forEach((textureDescriptor) => {
            layerArray.push(textureDescriptor);
            if (!textureDescriptor.url) {
                console.warn("Player resource has no URL", textureDescriptor);
                return;
            }
            const frameSize = getPlayerTextureFrameSize(textureDescriptor.id);
            load.spritesheet(textureDescriptor.id, textureDescriptor.url, {
                frameWidth: frameSize,
                frameHeight: frameSize,
            });
        });
        returnArray.push(layerArray);
    });
    return returnArray;
};
export const loadAllDefaultModels = (
    load: LoaderPlugin,
    playerTextures: PlayerTextures,
): WokaTextureDescriptionInterface[] => {
    const returnArray = Object.values(playerTextures.getTexturesResources(PlayerTexturesKey.Woka));
    returnArray.forEach((playerResource: WokaTextureDescriptionInterface) => {
        if (!playerResource.url) {
            console.warn("Player resource has no URL", playerResource);
            return;
        }
        const frameSize = getPlayerTextureFrameSize(playerResource.id);
        load.spritesheet(playerResource.id, playerResource.url, {
            frameWidth: frameSize,
            frameHeight: frameSize,
        });
    });
    return returnArray;
};

export const loadWokaTexture = (
    superLoaderPlugin: SuperLoaderPlugin,
    texture: WokaTextureDescriptionInterface,
): CancelablePromise<Texture> => {
    const frameSize = getPlayerTextureFrameSize(texture.id);

    return superLoaderPlugin.spritesheet(texture.id, texture.url, {
        frameWidth: frameSize,
        frameHeight: frameSize,
    });
};

export const lazyLoadPlayerCharacterTextures = (
    superLoaderPlugin: SuperLoaderPlugin,
    textures: WokaTextureDescriptionInterface[],
): CancelablePromise<string[]> => {
    const promisesList: CancelablePromise<Texture>[] = [];
    for (const texture of textures) {
        promisesList.push(
            superLoaderPlugin.spritesheet(texture.id, texture.url, {
                frameWidth: getPlayerTextureFrameSize(texture.id),
                frameHeight: getPlayerTextureFrameSize(texture.id),
            }),
        );
    }
    const returnPromise: CancelablePromise<Texture[]> = CancelablePromise.all(promisesList);

    return returnPromise.then(() =>
        textures.map((key) => {
            return key.id;
        }),
    );
};
